import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { createOrderApi, getWebxpayPaymentLinkApi } from '../../api/orders.api';
import { clearCart } from '../cart/cart.slice';
import { checkoutFailure, checkoutSuccess, placeOrderRequest } from './checkout.slice';
import type { RootState } from '../rootReducer';

const submitWebxpayPaymentForm = (
  paymentUrl: string,
  fields: Record<string, string | number | boolean | null | undefined>,
): void => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = paymentUrl;
  form.style.display = 'none';

  Object.entries(fields).forEach(([key, value]) => {
    if (key === 'route') return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value == null ? '' : String(value);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

const redirectToPaymentUrl = (paymentUrl: string): void => {
  window.location.assign(paymentUrl);
};

export function* checkoutWorker(action: any): SagaIterator {
  const { navigate, successPath, formData, cartItems } = action.payload;

  try {
    const auth = yield select((state: RootState) => state.auth);
    let userId = auth.user?.id;

    if (!userId || userId === 0) {
      const persisted = localStorage.getItem('auth');
      if (persisted) {
        const parsed = JSON.parse(persisted);
        const u = parsed.user;
        userId = u?.id || u?.ID || u?.user_id || (u?.data && u?.data.ID);
      }
    }

    const orderPayload = {
      payment_method: formData.paymentMethod,
      payment_method_title: formData.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Credit / Debit Card',
      set_paid: false,
      customer_id: userId && userId > 0 ? Number(userId) : undefined,
      billing: { 
        ...formData, 
        first_name: formData.firstName, 
        last_name: formData.lastName,
        address_1: formData.address1, 
        address_2: formData.address2 
      },
      shipping: { 
        ...formData, 
        first_name: formData.firstName, 
        last_name: formData.lastName,
        address_1: formData.address1,
        address_2: formData.address2
      },
      line_items: cartItems.map((item: any) => ({ product_id: item.id, quantity: item.quantity })),
    };

    console.log("Placing Order Payload:", orderPayload);

    const response = yield call(createOrderApi, orderPayload);
    console.log("Order Creation Response:", response);

    yield put(checkoutSuccess(response.data));

    if (formData.paymentMethod === 'webxpay') {
      const orderId = response?.data?.id;
      const orderKey = response?.data?.order_key;

      if (!orderId || !orderKey) {
        throw new Error('Order was created but its payment credentials are missing.');
      }

      const paymentLinkResponse = yield call(getWebxpayPaymentLinkApi, orderId, orderKey);
      const paymentUrl = paymentLinkResponse?.data?.payment_url;
      const paymentFields = paymentLinkResponse?.data?.fields;

      if (!paymentUrl) {
        throw new Error('WebXPay payment URL is missing from the server response.');
      }

      yield put(clearCart());

      if (paymentFields && Object.keys(paymentFields).length > 0) {
        yield call(submitWebxpayPaymentForm, paymentUrl, paymentFields);
      } else {
        yield call(redirectToPaymentUrl, paymentUrl);
      }
      return;
    }

    yield put(clearCart());

    if (navigate) yield call(navigate, successPath || '/my-account');
  } catch (error: any) {
    console.error("Checkout Error:", error);
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Unable to start checkout. Please try again.';
    yield put(checkoutFailure(errorMessage));
  }
}

export function* watchCheckoutActions(): SagaIterator {
  yield takeLatest(placeOrderRequest, checkoutWorker);
}
